import {
  HeroRoot,
  CopyColumn,
  ImageColumn,
  HeroFlex,
  Title,
  RecipesContainer,
  AddToCartContainer,
  ImageContainer,
  RadioWrapper,
  RecipeOption,
  ExtraInformation,
} from './Hero.styles'
import { FeedingGuidelinesTable } from '../FeedingGuidelinesTable/FeedingGuidelinesTable'
import { useTranslation } from 'hooks/useTranslation'
import { Text } from 'components/Text'
import { Button } from 'components/Button'
import { useState } from 'react'
import { Stack } from 'components/Stack'
import Image from 'next/image'
import nuggets from '../../../../../../../../public/assets/product-v2/freeze-dried-box/beef-nuggets-on-table.webp'
import { ChangeEvent } from 'react'
import { ProductVariant } from 'models/Product'
import { AddToCart } from 'features/ECommerce/hooks/useAddToCart'
import { FreezeDriedBoxId } from 'models/Box'
import {
  InformationCollapse,
  InformationCollapseType,
} from 'components/InformationCollapse'


type HeroProps = {
  products: ProductVariant[]
  onAdd: AddToCart
}

export const Hero = ({ products, onAdd }: HeroProps) => {
  const { t } = useTranslation()

  const [quantity, setQuantity] = useState<number>(1)

  const [selectedProduct, setSelectedProduct] = useState<ProductVariant>(
    () => products.find((product) => product.slug === FreezeDriedBoxId.CHICKEN) ?? products[0]
  )

  const handleQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
    const numbersOnly = e.target.value.replace(/\D/g, '')
    const singleNumber = numbersOnly[numbersOnly.length - 1]
    e.target.value = singleNumber
    const numberValue = parseInt(singleNumber)
    setQuantity(numberValue || 1)
  }

  const handleProductSelect = (productId: FreezeDriedBoxId) => {
    const product = products.find((product) => product.slug === productId)
    if (product) {
      setSelectedProduct(product)
    }
  }

  const infoList: InformationCollapseType[] = [
    {
      label: 'recipes:hero-v2.safety',
      description: 'recipes:freeze-dried.safety-description',
    },
    {
      label: 'products-v2:freeze-dried-box.hero.feeding-guidelines.label',
      description: 'products-v2:freeze-dried-box.hero.feeding-guidelines.description',
      extraContent: <FeedingGuidelinesTable />,
    },
  ]

  return (
    <HeroRoot id="hero">
      <HeroFlex>
        <CopyColumn>
          <Stack direction="column" gap={{ '@initial': '3', '@bp2': '4' }}>
            <Title>{t('products-v2:freeze-dried-box.hero.title')}</Title>
            <Stack direction="column" gap="1">
              <Text as="p" weight="semiBold">
                {t('products-v2:freeze-dried-box.hero.box-contents')}
              </Text>
              <Text as="p">{t('products-v2:freeze-dried-box.hero.short-description')}</Text>
            </Stack>
            <Stack direction="column">
              <Text as="p">{t('products-v2:freeze-dried-box.hero.description')}</Text>
            </Stack>
          </Stack>
          <RecipesContainer>
            <Text id="freeze-dried-raw-box-select-recipes-text">
              {t('products-v2:freeze-dried-box.hero.select-recipes')}
            </Text>
            <RadioWrapper
              role="radiogroup"
              aria-labelledby="freeze-dried-raw-box-select-recipes-text"
            >
              <RecipeOption
                onClick={() => handleProductSelect(FreezeDriedBoxId.CHICKEN)}
                isActive={selectedProduct.slug === FreezeDriedBoxId.CHICKEN || !selectedProduct}
                role="radio"
              >
                {t('products-v2:freeze-dried-box.hero.chicken')}
              </RecipeOption>
              <RecipeOption
                onClick={() => handleProductSelect(FreezeDriedBoxId.BEEF)}
                isActive={selectedProduct.slug === FreezeDriedBoxId.BEEF}
                role="radio"
              >
                {t('products-v2:freeze-dried-box.hero.beef')}
              </RecipeOption>
              <RecipeOption
                onClick={() => handleProductSelect(FreezeDriedBoxId.MIXED)}
                isActive={selectedProduct.slug === FreezeDriedBoxId.MIXED}
                role="radio"
              >
                {t('products-v2:freeze-dried-box.hero.mixed')}
              </RecipeOption>
            </RadioWrapper>
          </RecipesContainer>
          <AddToCartContainer>
            {selectedProduct && (
              <Text css={{ marginBottom: '$2' }} size="h3">
                ${selectedProduct.price.total / 100}
              </Text>
            )}
            <Stack gap="3" width="fullWidth">
              <input
                id="quantity"
                name="quantity"
                className="fd-box-quantity"
                onChange={(e) => handleQuantityChange(e)}
                type="text"
                pattern="\d*"
                value={quantity}
                aria-label="Quantity"
              />
              <Button
                onClick={() =>
                  selectedProduct &&
                  onAdd(selectedProduct.productVariantId, selectedProduct.productId, quantity, selectedProduct.name)
                }
                fullWidth
              >
                {t('products-v2:freeze-dried-box.hero.cta')}
              </Button>
            </Stack>
          </AddToCartContainer>
          <ExtraInformation className="d-only">
            <InformationCollapse v2 infoList={infoList} />
          </ExtraInformation>
        </CopyColumn>
        <ImageColumn>
          <ImageContainer>
            <Image
              priority={true}
              src={nuggets}
              alt={t('products-v2:freeze-dried-box.hero.image')}
            />
          </ImageContainer>
        </ImageColumn>
        <ExtraInformation className="m-only">
          <InformationCollapse v2 infoList={infoList} />
        </ExtraInformation>
      </HeroFlex>
    </HeroRoot>
  )
}

Hero.displayName = 'FreezeDriedBoxHero'
